<script setup>
import { WdsBreadCrumbs, WdsMapPin, WdsPillActive, WdsPropertyCard, WdsSheet } from '@wds/components';
import { LIVE_BUDGET_MISS, useSocialHub, withLiveBudget } from '@/composables/useSocialHub';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links.js';
import cityConfig from '@/config/city';
import { commonValues } from '@/config/common';
import { SOCIAL_HUB_FLAG, SOCIAL_HUB_LIVE_BUDGET_MS, toPositiveInteger } from '@/config/social-hub';
import { extractThreePiPropertyData, getHwDestinationLandingParams } from '@/lib/threePiTracking';

// ### MIDDLEWARE TO VALIDATE ROUTE
definePageMeta({ middleware: 'validate-city' });

const {
  $tracking,
  $url,
} = useNuxtApp();
const {
  t,
  locale,
  locales,
} = useI18n();

// ### COMPOSABLES
const route = useRoute();
const dateTime = useDateTime();
const { useRouteManager } = useRoutes();
const { useUrl } = useUrls();
const { appendThreePiQueryParams } = useThreePiQueryParams();
const { buildSeo } = generateSeo();
const {
  defineLocationSchema,
  defineFaqSchema,
  defineListOfPropertiesSchema,
  defineListOfPopularPlacesSchema,
} = generateSchemas();
const { useImage } = useImages();
const { useFormatter } = useFormatters();
const { useFilter } = useFilters();
const { useRating } = useRatings();
const { socialCues } = useSocialCues();
const { usePropertyList } = useProperties();
const {
  isExtraSmallScreen,
  isSmallOrMediumScreen,
  isLargeScreen,
} = useMq();

// ### COMPOSABLES - APIs
const { getCityLocationTranslatedById } = useApiTranslator();
const { getCityPageTags } = useApiSeo();
const {
  getCityReviewSummaries,
  getCountryTopCities,
  getCityDistricts,
  getCityProperties,
  getTopPropertiesByCityIdPerFilters,
  getCitySocialCues,
} = useApiCities();
const { getEventsByCityNameAndCountryName } = useApiEvents();
const {
  buildEventDetailsModel,
  getEventPageUrlFromRawEvent,
}
  = useEventDetailsModel();
const { hasOgEventsCityContent } = useOgEvents();

// ### STORES
const sessionStore = useSessionStore();
const locationStore = useLocationStore();
const languageStore = useLanguageStore();
const searchStore = useSearchStore();
const testingStore = useTestingStore();
const sustainabilityStore = useSustainabilityStore();
const {
  createDomMarker,
  preloadMapMarkerSdk,
} = useMapMarker();

// ### A/B TESTING FEATURE TOGGLES
const experimentCityMap = testingStore.hasEnabledFeature('static_cityMap');
const experimentNewSortOrder
  = testingStore.hasEnabledFeature('web_newSortOrder');
const experimentNewSortOrderParticipating = testingStore.hasEnabledFeature(
  'web_newSortOrderIsUserParticipating',
);
const experimentLinkups = testingStore.hasEnabledFeature('web_linkupsCallouts');
const experimentSocialCues = testingStore.hasEnabledFeature(
  'web_socialCuesCallouts',
);
const experimentSocialCuesParticipating = testingStore.hasEnabledFeature(
  'web_isUserParticipatingSocialCuesCallouts',
);
const experimentCityTopPropertiesByFilters = testingStore.hasEnabledFeature(
  'static_topPropertiesByFilter',
);
const experimentCityTopPropertiesByFiltersVar
  = testingStore.getFeatureVariables('static_topPropertiesByFilter');
const experimentSocialCuesCityBlock = testingStore.hasEnabledFeature(
  'web_socialCuesCalloutsCityBlock',
);
const experimentSocialCuesCityBlockParticipating
  = testingStore.hasEnabledFeature(
    'web_socialCuesCalloutsCityBlock_isParticipating',
  );
const experimentOgEvents = testingStore.hasEnabledFeature('web_enableOGevents');
const experimentThreePiProperties = testingStore.hasEnabledFeature(
  'static_enable3PIProperties',
);
const experimentThreePiCatalogApi = testingStore.hasEnabledFeature(
  'static_enable3PICatalogApi',
);
const experimentSocialHub = testingStore.hasEnabledFeature(SOCIAL_HUB_FLAG);
const {
  buildSocialHubViewModel,
  hasValidSocialHubRow,
  parseCitiesById,
  shouldSkipSocialHubLiveFetch,
  utcDateKey,
} = useSocialHub();
const citiesById = experimentSocialHub
  ? parseCitiesById(testingStore.getFeatureVariables(SOCIAL_HUB_FLAG))
  : {};

// ### CONSTANTS INITIALIZATION
const EVENTS_SIGNPOST_LIMIT = 4;
const locationKey = 'city';
const entryPageName = 'CITY';
const gtmPageName = 'staticFab';
const gtmClassification = 'fab';
const segmentTracking = SEGMENT_PAGE_DATA.city;

const transcodeInterpolate = new TranscodeInterpolate();
const urlParams = route?.params || null;
const urlQuery = route?.query || null;

// ### VARIABLES INITIALIZATION
const urlPageNumber = Number.parseInt(urlParams?.pageNumber) || null;
const activeFilter = useFilter.getCurrentFilter();
const mapMarkers = ref([]);
const {
  mountMapPinApp,
  mountPropertyCardApp,
} = useCityMapVueApps(mapMarkers);
const showMap = ref(false);
const mapSheetIsReady = ref(false);
let cityPageUrlFilter = null;
// Destination Landing Page Viewed variables, reused by the 3PI click event (set in onMounted).
let cityPageSegmentVariables = {};

// ### ROUTE VALIDATION
const propertyTypeObj = useRouteManager.validatePropertyTypeParam(
  urlParams.propertyType,
);
const propertyTypeKey = propertyTypeObj?.key;

const cityObj = await useRouteManager.validateCityParam(
  propertyTypeObj,
  urlParams.country,
  urlParams.city,
);
const urlData = useRouteManager.getUrlData(
  urlParams.propertyType,
  urlParams.continent,
  urlParams.country,
  urlParams.city,
);
useRouteManager.redirectRoute(urlData, urlQuery);

// ### PAGE NUMBER VALIDATION
// If the user requests for a non-existent page number,
// redirect to the original city/filter page
if (urlParams?.pageNumber) {
  const maxPageNumber = Math.ceil(
    cityObj?.totalPropertiesAvailable / cityConfig.maxNumberOfPropertiesPerPage,
  );
  if (
    urlPageNumber === null
    || urlPageNumber <= 1
    || urlPageNumber > maxPageNumber
  ) {
    const originalPage = route.href.replace(/\/p\/[^/]+\//, '/');
    navigateTo(originalPage, { redirectCode: 301 });
  }
}

const cityPageUrl = useUrl.getCityPageUrl(
  {
    propertyTypeSlug: propertyTypeObj?.slug,
    urlFriendlyContinent: cityObj?.urlFriendlyContinent,
    urlFriendlyCountry: cityObj?.urlFriendlyCountry,
    urlFriendlyCity: cityObj?.urlFriendlyName,
  },
  {},
  urlPageNumber,
);

if (activeFilter) {
  cityPageUrlFilter = useUrl.getCityPageUrl(
    {
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: cityObj?.urlFriendlyContinent,
      urlFriendlyCountry: cityObj?.urlFriendlyCountry,
      urlFriendlyCity: cityObj?.urlFriendlyName,
      urlFilterSlug: t(activeFilter?.slug),
    },
    {},
    urlPageNumber,
  );
}

const pageCanonicalUrl = activeFilter ? cityPageUrlFilter : cityPageUrl;

const cityFilterPathSegmentByIso = activeFilter
  ? Object.fromEntries(
    (locales?.value || []).map((lang) => [
      lang.isoUnder,
      useUrl.getCityFilterPathSegmentForIso(activeFilter.slug, lang.isoUnder),
    ]),
  )
  : null;
if (activeFilter && cityFilterPathSegmentByIso) {
  const currentIso = useFormatter.getLocaleObjectByCode(
    locale?.value,
  )?.isoUnder;
  if (currentIso) {
    cityFilterPathSegmentByIso[currentIso] = t(activeFilter.slug);
  }
}

const countryPageUrl = useUrl.getCountryPageUrl({
  propertyTypeSlug: propertyTypeObj?.slug,
  urlFriendlyContinent: cityObj?.urlFriendlyContinent,
  urlFriendlyCountry: cityObj?.urlFriendlyCountry,
});
const cityFullLocationInfo = {
  id: cityObj.id,
  city: cityObj.name,
  // English names for the 3PI q token (destinationcity/destinationcountry).
  cityEn: cityObj?.tracking?.name,
  countryEn: cityObj?.tracking?.country,
  urlFriendlyCity: cityObj?.urlFriendlyName,
  country: cityObj.country,
  countryId: cityObj.countryId,
  urlFriendlyCountry: cityObj?.urlFriendlyCountry,
  continent: cityObj.continent,
  continentId: cityObj.continentId,
  urlFriendlyContinent: cityObj?.urlFriendlyContinent,
};

const isCityOgEventsCandidate
  = experimentOgEvents
    && locale?.value === 'en'
    && !activeFilter
    && !urlPageNumber;

const cityOgEventsLocationData = {
  continent: { urlFriendlyName: cityObj?.urlFriendlyContinent },
  country: {
    urlFriendlyName: cityObj?.urlFriendlyCountry,
    name: cityObj?.country,
  },
  city: {
    urlFriendlyName: cityObj?.urlFriendlyName,
    name: cityObj?.name,
  },
};

const hasSocialHubCityRow = Boolean(
  experimentSocialHub
  && cityObj?.id
  && !activeFilter
  && !urlPageNumber
  && citiesById?.[String(cityObj.id)],
);
const isSocialHubCandidate = hasSocialHubCityRow && hasValidSocialHubRow(citiesById, cityObj.id);

const { getCitySocialStats } = useApiTravelerProfile();
const socialHubCityId = Number(cityObj?.id);
const socialHubBudgetMissCityId = useState('social-hub-budget-miss-city-id', () => null);
const socialHubCachedViewModel = useState('social-hub-view-model', () => null);
const socialHubDateKey = useState('social-hub-date-key', () => utcDateKey());
const isHydratingSocialHub = Boolean(import.meta.client && useNuxtApp().isHydrating);
const skipSocialHubLiveFetch = shouldSkipSocialHubLiveFetch({
  isHydrating: isHydratingSocialHub,
  missedCityId: socialHubBudgetMissCityId.value,
  cachedViewModel: socialHubCachedViewModel.value,
  cityId: socialHubCityId,
});
const socialHubLivePromise = isSocialHubCandidate && !skipSocialHubLiveFetch
  ? getCitySocialStats(socialHubCityId)
  : null;

const [urlTranslations, citySocialCues, hasCityOgEventsContent, liveOrMiss]
  = await Promise.all([
    getCityLocationTranslatedById(
      cityObj?.continentId,
      cityObj?.countryId,
      cityObj?.id,
    ),
    getCitySocialCues(cityObj?.id),
    isCityOgEventsCandidate
      ? hasOgEventsCityContent(cityObj?.id)
      : Promise.resolve(false),
    socialHubLivePromise
      ? withLiveBudget(socialHubLivePromise, SOCIAL_HUB_LIVE_BUDGET_MS)
      : Promise.resolve(null),
  ]);

let socialHubViewModel = isHydratingSocialHub
  && Number(socialHubCachedViewModel.value?.cityId) === socialHubCityId
  ? socialHubCachedViewModel.value.viewModel
  : null;

if (isSocialHubCandidate && !skipSocialHubLiveFetch) {
  const budgetMissed = liveOrMiss === LIVE_BUDGET_MISS;
  const live = budgetMissed ? null : liveOrMiss;

  if (budgetMissed) {
    socialHubBudgetMissCityId.value = socialHubCityId;
    socialHubCachedViewModel.value = null;
    console.error('### Social Hub live budget exceeded', {
      cityId: cityObj.id,
      budgetMs: SOCIAL_HUB_LIVE_BUDGET_MS,
    });
  } else {
    socialHubBudgetMissCityId.value = null;
  }

  socialHubViewModel = buildSocialHubViewModel({
    cityId: socialHubCityId,
    cityName: cityObj.name,
    citiesById,
    liveMembersCount: live?.members_count,
    dateKey: socialHubDateKey.value,
  });

  socialHubCachedViewModel.value = socialHubViewModel
    ? {
      cityId: socialHubCityId,
      viewModel: socialHubViewModel,
    }
    : null;

  if (!socialHubViewModel && !budgetMissed) {
    console.error('### Social Hub hidden', {
      cityId: cityObj.id,
      hasCityRow: true,
      hasLiveCount: toPositiveInteger(live?.members_count) !== null,
    });
  }
} else if (!isSocialHubCandidate) {
  socialHubCachedViewModel.value = null;
  if (hasSocialHubCityRow) {
    console.error('### Social Hub hidden', {
      cityId: cityObj.id,
      hasCityRow: true,
      hasValidRow: false,
    });
  }
}

const socialHubSrpHref = socialHubViewModel
  ? useUrl.getCityPageUrlDynamic({
    cityId: Number(cityObj.id),
    cityName: cityObj?.name,
    countryName: cityObj?.country,
    searchString: cityObj?.name && cityObj?.country
      ? `${cityObj.name}, ${cityObj.country}`
      : `${cityObj.urlFriendlyName}, ${cityObj.urlFriendlyCountry}`,
  })
  : '';

const shouldFetchCityOgEvents = isCityOgEventsCandidate && hasCityOgEventsContent;

const locationEventsResult = shouldFetchCityOgEvents
  ? await getEventsByCityNameAndCountryName(
    cityObj?.urlFriendlyName,
    cityObj?.urlFriendlyCountry,
    EVENTS_SIGNPOST_LIMIT,
  ).catch(() => ({}))
  : {};

const hrefUrls = useUrl.generateHrefLangForCity(
  urlTranslations,
  propertyTypeKey,
  activeFilter?.slug,
  {
    pageNr: urlPageNumber,
    filterPathSegmentByIso: cityFilterPathSegmentByIso,
  },
);

let cityOgEventsList = [];

if (shouldFetchCityOgEvents) {
  const locationEventsList = Array.isArray(locationEventsResult?.events)
    ? locationEventsResult.events
    : [];
  const eventsRootSlug = t('t_OGEVENTSSLUG');

  cityOgEventsList = locationEventsList
    .map((rawEvent) => {
      const enrichedEvent = {
        ...rawEvent,
        location: {
          ...(rawEvent?.location && typeof rawEvent.location === 'object'
            ? rawEvent.location
            : {}),
          ...cityOgEventsLocationData,
        },
      };
      const pageUrl = getEventPageUrlFromRawEvent(
        enrichedEvent,
        eventsRootSlug,
      );
      return buildEventDetailsModel(enrichedEvent, pageUrl);
    })
    .filter((model) => model !== null);
}

const citySocialCuesCount = citySocialCues?.guestCount || 0;

// ### CITY HERO IMAGE
const cityHeroImageObj = {
  mobile: cityObj?.image?.mobile,
  tablet: cityObj?.image?.tablet,
  desktop: cityObj?.image?.desktop,
  heroImage: cityObj?.image?.heroImage,
};

// ### CITY BREADCRUMBS
const crumbs = [
  {
    label: t('t_A11YBCTITLEPROPERTYTYPE', { PROPERTYTYPE: t(propertyTypeObj?.transcode) }),
    title: t(propertyTypeObj.transcode),
    link: useUrl.getWorldPageUrl({ propertyTypeSlug: propertyTypeObj?.slug }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: cityObj?.continent,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: cityObj?.continent,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getContinentPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: cityObj?.urlFriendlyContinent,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: cityObj?.country,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: cityObj?.country,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getCountryPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: cityObj?.urlFriendlyContinent,
      urlFriendlyCountry: cityObj?.urlFriendlyCountry,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: cityObj?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: cityObj?.name,
    link: cityPageUrl,
  },
];
if (activeFilter) {
  crumbs.push({
    label: t(activeFilter?.transcode_alttext),
    title: t(activeFilter?.transcode_title),
    link: cityPageUrlFilter,
  });
}

// ### CITY PROPERTY LIST
const cityAllProperties = await getCityProperties(
  propertyTypeKey,
  cityObj?.id,
  urlPageNumber,
  cityConfig.maxNumberOfPropertiesPerPage,
  0,
  activeFilter?.key,
  cityObj,
);

cityObj.totalPropertiesCount = cityAllProperties?.totalPropertiesCount || 0;
const cityResultCount = cityObj?.totalPropertiesCount || cityObj?.totalPropertiesAvailable || 0;
// COD-877: 3PI experiment meta for tracking (see useApiCities.getCityProperties).
const cityThreePiData = cityAllProperties?.threePi || {};

// ### CITY PROPERTIES PLACEHOLDERS
usePropertyList.setPropertyList(cityAllProperties?.properties);
const cityTopCheapestPropertiesPlaceholders = usePropertyList.getTopCheapestPropertiesForPlaceholders.value || [];
const cityTopRankedPropertiesPlaceholders = usePropertyList.getTopRankedPropertiesForPlaceholders.value || [];

// ### CITY FEATURED PROPERTY LIST
const cityFeaturedProperties = await getCityProperties(
  propertyTypeKey,
  cityObj?.id,
  1,
  5,
  1,
  activeFilter?.key,
);

// ### CITY POPULAR PLACES  (AKA DISTRICTS)
const cityPopularPlaces = await getCityDistricts(propertyTypeKey, cityObj?.id);
const cityPopularPlacesList = cityPopularPlaces?.reduce((list, location) => {
  const locationData = {
    propertyTypeSlug: propertyTypeObj.slug,
    urlFriendlyContinent: cityObj?.urlFriendlyContinent,
    urlFriendlyCountry: cityObj?.urlFriendlyCountry,
    urlFriendlyCity: cityObj?.urlFriendlyName,
    urlFriendlyDistrict: location.urlFriendlyName,
  };

  list.push({
    id: location.id,
    name: location.name,
    label: location.name,
    title: location.name,
    url: useUrl.getDistrictPageUrl(locationData),
    urlFriendlyName: location.urlFriendlyName,
    totalPropertiesAvailable: location.totalPropertiesAvailable,
    propertyTypeSlug: propertyTypeObj.slug,
    pricesFromFormatted:
      location?.pricesFrom?.value > 0
        ? useFormatter.formatCurrency(
          location.pricesFrom.value,
          location.pricesFrom.currency,
        )
        : null,
  });
  return list;
}, []);

// ### OTHER LOCATIONS IN COUNTRY OF CITY
const countryTopCities = (await getCountryTopCities(cityObj?.countryId)) || [];
const cityOtherLocations = countryTopCities
  .filter((topCity) => topCity?.id !== cityObj?.id)
  .map((topCity) => {
    const locationData = {
      propertyTypeSlug: propertyTypeObj.slug,
      urlFriendlyContinent: topCity.urlFriendlyContinent,
      urlFriendlyCountry: topCity.urlFriendlyCountry,
      urlFriendlyCity: topCity.urlFriendlyName,
    };
    return {
      id: topCity.id,
      name: topCity.name,
      label: topCity.name,
      title: topCity.name,
      url: useUrl.getCityPageUrl(locationData),
    };
  });

// ### CITY RATING AND REVIEWS
const reviewSummaries = await getCityReviewSummaries(cityObj?.id);
cityObj.ratingBreakdown = reviewSummaries?.ratings || {};

// ### TOP CITY PROPERTIES PER FILTER
let topPropertiesByCityIdPerFilters = {};
if (experimentCityTopPropertiesByFilters && propertyTypeKey === 'hostel') {
  const allowedCityList
    = experimentCityTopPropertiesByFiltersVar?.allowed_city_list?.split(',');
  const allowedCityListId = allowedCityList?.includes(cityObj?.id.toString());
  if (
    allowedCityListId
    || experimentCityTopPropertiesByFiltersVar?.allowed_city_list.length === 0
  ) {
    topPropertiesByCityIdPerFilters = await getTopPropertiesByCityIdPerFilters(
      propertyTypeKey,
      cityObj?.id,
    );
  }
}
// ### CITY SEO DATA
const seoData = await getCityPageTags(propertyTypeKey, cityObj?.id);
const seoExtraData = {
  canonicalUrl: pageCanonicalUrl,
  ogImage: useImage.getImage(cityHeroImageObj?.desktop) || '',
};

if (!cityResultCount) {
  seoData.title = t('t_PROPERTYTYPEZEROINLOCATION');
  seoData.ogTitle = t('t_PROPERTYTYPEZEROINLOCATION');
}

const tradReplacements = {
  CITY: cityObj?.name,
  CHOSENCONTINENT: cityObj?.name,
  CHOSENCOUNTRY: cityObj?.name,
  CHOSENCITY: cityObj?.name,
  LOCATIONNAME: cityObj?.name,
  COUNTRY: cityObj?.country,
  COUNTRYSEARCHURL: countryPageUrl,
  SERVERNAME: commonValues.HOSTELWORLD,
  PROPCOUNT: cityResultCount,
  REVIEWCOUNT: cityObj?.numberOfReviews,
  REVIEWSCOUNT: cityObj?.numberOfReviews,
  PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(propertyTypeObj?.transcode),
  YEAR: new Date().getFullYear(),
  ACTIVEFILTER: activeFilter ? ` ${t(activeFilter?.transcode_text)}` : '',
};

// ### ADD CITY TOP RANKED PROPERTIES TO PLACEHOLDER REPLACEMENTS
if (Array.isArray(cityTopRankedPropertiesPlaceholders) && cityTopRankedPropertiesPlaceholders.length) {
  Object.assign(tradReplacements, ...cityTopRankedPropertiesPlaceholders);
}

// ### ADD CITY TOP CHEAPEST PROPERTIES TO PLACEHOLDER REPLACEMENTS
if (Array.isArray(cityTopCheapestPropertiesPlaceholders) && cityTopCheapestPropertiesPlaceholders.length) {
  Object.assign(tradReplacements, ...cityTopCheapestPropertiesPlaceholders);
}

if (seoData?.faqContent && Object.keys(seoData?.faqContent).length) {
  Object.keys(seoData.faqContent).forEach((faqEntry) => {
    return seoData.faqContent[faqEntry]
      ? (seoData.faqContent[faqEntry] = transcodeInterpolate.interpolate(
        seoData.faqContent[faqEntry],
        tradReplacements,
        ['##', '##'],
      ))
      : (seoData.faqContent[faqEntry] = '');
  });
}

const seoMetaData = buildSeo.getMetaData(
  seoData,
  seoExtraData,
  tradReplacements,
  false,
  locationKey,
);

// ### ABOUT CITY
const aboutCityDescription = $url.processHtmlContent(seoMetaData?.copy);

// ### COMPUTED
const getMainTitle = computed(() => {
  return (
    seoMetaData.h1
    || t('t_PROPERTYTYPEWITHFILTERINLOCATION', {
      PROPERTYTYPE: t(propertyTypeObj.transcode),
      LOCATION: cityObj?.name,
      ACTIVEFILTER: activeFilter ? ` ${t(activeFilter?.transcode_text)}` : '',
    })
  );
});

const getPropertiesCountText = computed(() => {
  return t('t_LOCATIONPROPERTIESNUMBER', {
    PROPERTYTYPE: t(propertyTypeObj.transcode),
    NUMPROPERTIES: cityObj?.totalPropertiesCount,
    CITYNAME: cityObj?.name,
    COUNTRYNAME: cityObj?.country,
    LOCATION: `${cityObj?.name}, ${cityObj?.continent}`,
    ACTIVEFILTER: activeFilter ? ` ${t(activeFilter?.transcode_text)}` : '',
  });
});

const hasFreeCancellation = computed(() => {
  return cityObj?.freeCancellation?.isAvailable;
});

const getFreeCancellationDay = computed(() => {
  return cityObj?.freeCancellation?.daysInAdvance;
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isExtraSmallScreen) ? 2 : null;
});

const hasFilterOptions = computed(() => {
  return (
    cityObj?.numberOfProperties
    >= cityConfig.minNumberOfPropertiesToAllowFilters
  );
});

const hasPopularPlaces = computed(() => {
  return cityPopularPlacesList?.length > 0;
});

const hasOtherLocations = computed(() => {
  return cityOtherLocations?.length > 0;
});

const getFaqContent = computed(() => {
  const faqContent = seoData?.faqContent || {};
  const newFaqContent = [];

  if (Object.keys(faqContent)) {
    Object.keys(faqContent).reduce((newFaqContent, faqKey) => {
      if (!faqKey.match(/ response$/) && !!faqContent[faqKey]) {
        const faqHeader = $url.processHtmlContent(faqContent[faqKey]);
        const faqResponse = $url.processHtmlContent(
          faqContent[`${faqKey} response`],
        );

        newFaqContent.push({
          header: faqHeader?.processedContent || faqContent[faqKey],
          content:
            faqResponse?.processedContent || faqContent[`${faqKey} response`],
        });
      }
      return newFaqContent;
    }, newFaqContent);
  }

  return newFaqContent;
});

const hasFaq = computed(() => {
  return getFaqContent?.value?.length > 0;
});

const hasProperties = computed(() => {
  return cityObj?.totalPropertiesCount > 0;
});

const isFirstPage = computed(() => {
  return cityAllProperties?.page === 1;
});

const getMapConfigs = computed(() => {
  const cityMapCenter = [
    cityObj?.geoCoordinates?.longitude,
    cityObj?.geoCoordinates?.latitude,
  ];

  return {
    scrollZoom: false,
    center: cityMapCenter,
    zoom: 6,
  };
});

const getMapButtonParams = computed(() => {
  const humanReadableSearchQuery
    = cityObj?.name && cityObj?.country
      ? `${cityObj.name}, ${cityObj.country}`
      : `${cityObj.urlFriendlyName}, ${cityObj.urlFriendlyCountry}`;

  const location = {
    cityId: cityObj.id,
    cityName: cityObj?.name,
    countryName: cityObj?.country,
    searchString: humanReadableSearchQuery,
  };

  return {
    text: t('t_MAP'),
    iconStart: 'map',
    iconEnd:
      experimentCityMap && !toValue(isSmallOrMediumScreen) && showMap.value
        ? 'close'
        : '',
    href: experimentCityMap
      ? ''
      : useUrl.getCityPageUrlDynamic(location, { display: 'map' }),
  };
});

const getAppDownloadDetails = computed(() => {
  const searchParamsCheckIn = dateTime.formatToFlat(
    searchStore.getSearchCheckIn,
  );
  const searchParamsCheckOut = dateTime.formatToFlat(
    searchStore.getSearchCheckOut,
  );

  const searchParamsGroupType = searchStore.getSearchGroupType?.value || null;

  let appLink = `cy/${cityObj.id}?from=${searchParamsCheckIn}&to=${searchParamsCheckOut}&guests=${searchStore.getSearchNumberOfGuests}`;

  if (searchParamsGroupType) {
    appLink += `&groupType=${searchParamsGroupType}&groupAgeRange=${searchStore.getSearchGroupAge}`;
  }

  return {
    page: 'city',
    linkPaths: APPDOWNLOADBANNERLINKS?.CITY,
    linkParams: appLink,
  };
});

const cityDisplayableSocialCues = computed(() => {
  const citySocialCues = citySocialCuesCount || 0;

  if (!citySocialCues || citySocialCues < 10) {
    return null;
  }
  if (citySocialCues < 100) {
    return Math.floor(citySocialCues / 10) * 10;
  }
  return Math.floor(citySocialCues / 50) * 50;
});

const getPropertiesWithSocialCues = computed(() => {
  return (
    cityAllProperties?.properties?.filter(
      (p) => p?.type !== '3PI' && p?.whosGoing?.total,
    ) || []
  );
});

const getPropertiesWithLinkups = computed(() => {
  return cityAllProperties?.properties?.filter(
    (p) => p?.type !== '3PI' && p.linkups && Object.keys(p.linkups).length > 0,
  );
});

// ### METHODS - PROPERTY LIST
// Display position where the 3PI block starts (3PI properties are appended after the HW ones).
function getThreePiPositionStart() {
  const threePiCount = cityThreePiData?.properties?.length || 0;
  return (cityAllProperties?.properties?.length || 0) - threePiCount + 1;
}

function handleThirdPartyPropertyClick({
  property,
  position,
}) {
  $tracking?.onThirdPartyPropertyClicked({
    ...cityPageSegmentVariables,
    page_number: cityAllProperties?.page || 1,
    product_id: property?.id?.toString() || '',
    vendor_product_id: property?.id?.toString() || '',
    provider_product_id: property?.providerId?.toString() || '',
    position,
    is_third_party_property: true,
    property_category: property?.propertyCategory || '',
    third_party_properties_estimated: cityThreePiData?.estimatedCount || 0,
    third_party_properties_displayed: cityThreePiData?.properties?.length || 0,
    total_third_party_properties: cityThreePiData?.totalCount || 0,
    third_party_page_position_start: getThreePiPositionStart(),
    ...extractThreePiPropertyData(cityThreePiData?.properties),
  });
}

function handleSustainabilityPinClick(propertySustainabilityInfo) {
  sustainabilityStore.setSustainabilityDetails(propertySustainabilityInfo);

  const trackingInfo = {
    gtm: {
      parameter1: `SUSTAINABILITY_TIER_${propertySustainabilityInfo?.tier}`,
      parameter2: gtmPageName,
    },
  };

  $tracking.onSustainabilityIconClick(trackingInfo.gtm, trackingInfo.segment);
}

// ### METHODS - CITY MAP
function handleMapButtonClick() {
  if (experimentCityMap) {
    showMap.value = !showMap.value;
  }
}

function onLinkupsPillClick(property) {
  const trackingInfo = {
    segment: {
      action: 'Linkups Property Cards Pills Clicked',
      linkups_web_cohort: experimentLinkups ? 'variation' : 'control',
      social_cues_block_flag:
        !!experimentSocialCuesCityBlockParticipating
        && !!experimentSocialCuesCityBlock,
      linkups_property_cards_flag: experimentLinkups,
      linkups_count_clicked: property?.linkupsCount,
      linkups_property_clicked: property?.id,
    },
  };

  $tracking.triggerLinkupEventAction(trackingInfo.segment);
}

function handleSocialCuesClick(property) {
  const trackingInfo = {
    segment: {
      action: 'Social Cues Property Cards Pills Clicked',
      social_cues_web_cohort: experimentSocialCues ? 'variation' : 'control',
      social_cues_block_flag:
        !!experimentSocialCuesCityBlockParticipating
        && !!experimentSocialCuesCityBlock,
      social_cues_count_clicked: property?.socialCuesCount,
      social_cues_property_clicked: property?.id,
    },
  };

  $tracking.triggerSocialCuesEventAction(trackingInfo.segment);
}

function getMapPropertyUrl(property) {
  if (property?.type === '3PI') {
    const externalUrl = useUrl.get3PIPropertyPageUrl(property?.urlPath);
    return appendThreePiQueryParams(externalUrl, property, cityFullLocationInfo) || externalUrl;
  }

  const propertyTypeInfo = findPropertyTypeByKey(property.type);
  return useUrl.getPropertyPageUrl({
    propertyTypeSlug: t(propertyTypeInfo.slug),
    propertyId: property.id,
    urlFriendlyProperty: property.urlFriendlyName,
  });
}

function handlePinClick(property) {
  const propertyCardTemplate = defineComponent(WdsPropertyCard);
  const propertyUrl = getMapPropertyUrl(property);
  const propertyCardTarget = document.querySelector(
    '.city-map-container .city-map-property-card',
  );
  mountPropertyCardApp(
    propertyCardTemplate,
    {
      name: property.name,
      layout: 'compact',
      orientation: 'horizontal',
      href: propertyUrl,
      gallery: {
        slides: property.image?.small ? [useImage.getImage(property.image.small)] : [],
        alt: property.name,
        title: property.name,
      },
      rating: {
        score: property.avgRating,
        totalReviews: property.numberReviews,
        translations: toValue(useRating.getRatingScoreKeywordTranslations),
      },
      fromPrice: { curPrice: property.minPriceString },
    },
    propertyCardTarget,
  );

  mapMarkers.value.forEach((m) => {
    const markerPin = m.pin;
    const isClickedMarker = markerPin.id === property.id;
    const propertyCard = document.querySelector(
      `.property-listing-cards .property-listing-card[data-pkey='p${markerPin.id}']`,
    );

    markerPin
      .getElement()
      .querySelector('.pin-wrapper')
      .classList
      .toggle('theme-blue', isClickedMarker);
    markerPin.getElement().style.zIndex = isClickedMarker ? 1 : 'initial';
    propertyCard?.classList?.remove('selected');

    if (isClickedMarker) {
      markerPin._map.panTo(m.pin.getLngLat());

      // Check if propertyCard exists and isSmallOrMediumScreen is false
      if (propertyCard && !isSmallOrMediumScreen.value) {
        propertyCard.classList.add('selected');

        // Use setTimeout to ensure that the browser has finished rendering before scrolling
        setTimeout(() => {
          propertyCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 0);
      }
    }
  });
}

async function buildPropertyMapPin(property) {
  if (import.meta.server) {
    return null;
  }

  const mapPinTemplate = defineComponent(WdsMapPin);
  const markerLongitude = Number(property?.longitude);
  const markerLatitude = Number(property?.latitude);

  if (!Number.isFinite(markerLongitude) || !Number.isFinite(markerLatitude)) {
    return null;
  }

  const markerContainer = document.createElement('div');

  mountMapPinApp(
    mapPinTemplate,
    {
      ratingScore: property.avgRating,
      price: property.minPriceString,
      onClick: () => handlePinClick(property),
    },
    markerContainer,
  );

  const newMarker = await createDomMarker({
    element: markerContainer,
    anchor: 'bottom',
    lngLat: [markerLongitude, markerLatitude],
  });

  if (!newMarker) {
    return null;
  }

  newMarker.id = property.id;
  return newMarker;
}

const MAP_MARKER_BATCH_SIZE = 20;

async function setupCityMapMarkers() {
  const cityPropertiesForMap = cityAllProperties?.properties || [];
  const markerJobs = [];

  for (const property of cityPropertiesForMap) {
    const {
      id,
      latitude,
      longitude,
      sharedMinPrice,
      privateMinPrice,
    }
      = property;

    if (
      !Number.isFinite(Number(latitude))
      || !Number.isFinite(Number(longitude))
      || latitude == null
      || longitude == null
    ) {
      continue;
    }

    const privateRoomsPrice = privateMinPrice?.value || 0;
    const dormRoomsPrice = sharedMinPrice?.value || 0;
    const propertyMinPrice = Math.min.apply(
      0,
      [privateRoomsPrice, dormRoomsPrice].filter(Number),
    );

    if (propertyMinPrice === Infinity && property?.type !== '3PI') {
      continue;
    }

    const propertyCurrency
      = sharedMinPrice?.currency || privateMinPrice?.currency;
    const propertyMinPriceFormatted
      = Number.isFinite(propertyMinPrice) && propertyMinPrice > 0
        ? useFormatter.formatCurrency(propertyMinPrice, propertyCurrency)
        : '';
    property.minPriceString = propertyMinPriceFormatted;
    markerJobs.push({
      id,
      latitude,
      longitude,
      property,
    });
  }

  await preloadMapMarkerSdk();

  const loadedMarkers = [];

  for (
    let index = 0;
    index < markerJobs.length;
    index += MAP_MARKER_BATCH_SIZE
  ) {
    const batch = markerJobs.slice(index, index + MAP_MARKER_BATCH_SIZE);
    const batchMarkers = await Promise.all(
      batch.map(async (markerJob) => {
        const pin = await buildPropertyMapPin(markerJob.property);

        if (!pin) {
          return null;
        }

        return {
          id: markerJob.id,
          latitude: markerJob.latitude,
          longitude: markerJob.longitude,
          numProperties: 1,
          pin,
        };
      }),
    );

    loadedMarkers.push(...batchMarkers.filter(Boolean));
  }

  mapMarkers.value = loadedMarkers;
}

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(hrefUrls),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### LOCATION
locationStore.setLocation(locationKey, {
  id: cityObj?.id,
  name: cityObj?.name,
  image: cityHeroImageObj,
});
locationStore.setLocation('country', {
  id: cityObj?.countryId,
  name: cityObj?.country,
  image: cityHeroImageObj,
});

// ### SCHEMAS
const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({
    '@id': '#hostelworldNavigation',
    'name': 'Breadcrumb',
    'itemListElement': schemaCrumbs,
  }),
  defineLocationSchema('City', cityObj, seoMetaData),
  defineFaqSchema(hasFaq.value ? getFaqContent?.value : null),
  defineListOfPropertiesSchema(
    cityAllProperties?.properties,
    cityObj,
    propertyTypeObj,
  ),
  defineListOfPopularPlacesSchema(
    cityPopularPlacesList,
    cityObj,
    propertyTypeObj,
    seoMetaData,
  ),
]);

// ### TRACKING
sessionStore.setEntryPoint(entryPageName);

// ### MOUNTED
onMounted(async () => {
  showMap.value = !isSmallOrMediumScreen;
  if (experimentCityMap) {
    await setupCityMapMarkers();
  }

  const trackingPropertyType = propertyTypeObj?.type?.toLowerCase();
  const trackingContinentName = cityObj?.tracking?.continent;
  const trackingCountryName = cityObj?.tracking?.country;
  const trackingCityName = cityObj?.tracking?.name;

  const top5PropertyCards = usePropertyList.getTop5PropertiesForTracking.value;
  const hwProperties = usePropertyList.getHostelworldProperties.value;
  const top30Properties = usePropertyList.getFlatListOfTop30Properties?.value;
  const {
    number_results: hwResultCount,
    top_30_properties: top30PropertiesParam,
  } = getHwDestinationLandingParams(
    cityThreePiData?.hwCount ?? cityResultCount,
    top30Properties?.ids,
  );

  const listOfFeaturedIds
    = cityFeaturedProperties?.properties?.map((p) => p.id) || [];
  const listOfFeaturedFlags = hwProperties.map((p) =>
    listOfFeaturedIds.includes(p.id) ? 1 : 0,
  );

  const listOfLinkupsCount = hwProperties.map((p) => p?.linkups?.linkupsCount || 0);

  const topPrivateRoomsProperty
    = topPropertiesByCityIdPerFilters['private-room']?.properties
      ?.map((p) => p.id)
      .join('|') || 0;
  const topSoloTravellersProperty
    = topPropertiesByCityIdPerFilters['solo-travellers']?.properties
      ?.map((p) => p.id)
      .join('|') || 0;

  const sortOrderExperimentVariation = experimentNewSortOrder
    ? 'variation'
    : 'control';

  const trackingInfo = {
    gtm: {
      'gtmPropertyType': trackingPropertyType,
      'gtmCanonicalUrl': seoMetaData?.canonicalUrl,
      'gtmContinent': trackingContinentName,
      'gtmCountry': trackingCountryName,
      'gtmCity': trackingCityName,
      gtmPageName,
      gtmClassification,
      'gtmArrivalDate': dateTime.formatToFlat(searchStore.getSearchCheckIn),
      'gtmArrivalDateStripped': dateTime
        .formatToFlat(searchStore.getSearchCheckIn)
        .replace(/-/g, ''),
      'gtmDepartureDate': dateTime.formatToFlat(searchStore.getSearchCheckOut),
      'gtmDepartureDateStripped': dateTime
        .formatToFlat(searchStore.getSearchCheckOut)
        .replace(/-/g, ''),
      'gtmSearchKeywords': searchStore.getSearchPhrase || '',
      'gtmNumberResults': hwResultCount,
      'gtmFABAlgorithm': 'sfab',
      'gtmSortOrder': 'sfab',
      'gtmFeaturedListingImpression': listOfFeaturedIds?.length
        ? listOfFeaturedIds.join('|')
        : 0,
      'gtmTop30Properties': top30PropertiesParam || '',
      'gtmPropertyIds': usePropertyList.getFlatListOfIds.value || 0,
      'gtmFABProperties6-10': usePropertyList.getFlatListOf6To10Ids.value || '',
      'gtmFABPropertyCard1':
        top5PropertyCards && top5PropertyCards[0] ? top5PropertyCards[0] : '',
      'gtmFABPropertyCard2':
        top5PropertyCards && top5PropertyCards[1] ? top5PropertyCards[1] : '',
      'gtmFABPropertyCard3':
        top5PropertyCards && top5PropertyCards[2] ? top5PropertyCards[2] : '',
      'gtmFABPropertyCard4':
        top5PropertyCards && top5PropertyCards[3] ? top5PropertyCards[3] : '',
      'gtmFABPropertyCard5':
        top5PropertyCards && top5PropertyCards[4] ? top5PropertyCards[4] : '',
    },
    segment: {
      event_name: segmentTracking.VIEWED,
      page_type: segmentTracking.TYPE,
      property_type: trackingPropertyType,
      destination_continent: trackingContinentName,
      destination_country: trackingCountryName,
      destination_city: trackingCityName,
      destination_city_id: cityObj?.id?.toString(),
      number_results: hwResultCount,
      top_30_properties: top30PropertiesParam,
      top_30_privates_from_prices:
        top30Properties?.private_prices?.join('|') || '',
      top_30_dorms_from_prices:
        top30Properties?.dorms_prices?.join('|') || '',
      free_breakfast:
        top30Properties?.free_breakfast?.join('|') || '',
      free_wifi:
        top30Properties?.free_wifi?.join('|') || '',
      covid_icon:
        top30Properties?.covid_icon?.join('|') || '',
      hoscar_icon:
        top30Properties?.hoscar_icon?.join('|') || '',
      esg_label:
        top30Properties?.esg_label?.join('|') || '',
      featured_listing_properties: listOfFeaturedIds?.length
        ? listOfFeaturedIds.join('|')
        : 0,
      featured_listing_flag: listOfFeaturedFlags?.length
        ? listOfFeaturedFlags.join('|')
        : 0,
      auto_sort_order_applied: 'sfab',
      page_sort_order_applied: 'sfab',
      sfab_test_id_2: experimentNewSortOrderParticipating
        ? sortOrderExperimentVariation
        : 'non-participant',
      sfab_test_id_1: experimentCityTopPropertiesByFilters
        ? 'variation'
        : 'control',
      top_private_rooms_properties: topPrivateRoomsProperty,
      top_solo_travellers_properties: topSoloTravellersProperty,
      linkups_web_cohort: experimentLinkups ? 'variation' : 'control',
      linkups_property_cards_flag: experimentLinkups,
      linkups_event_count: listOfLinkupsCount?.length
        ? listOfLinkupsCount.join('|')
        : 0,
      linkups_properties:
        toValue(getPropertiesWithLinkups)
          ?.map((p) => p.id)
          .join('|') || '',
    },
  };

  if (experimentSocialCuesCityBlockParticipating) {
    trackingInfo.segment = {
      ...trackingInfo.segment,
      social_cues_web_block_cohort: experimentSocialCuesCityBlock
        ? 'variation'
        : 'control',
      social_cues_block_flag: !!experimentSocialCuesCityBlock,
      social_cues_block_count_displayed: cityDisplayableSocialCues?.value || 0,
    };
  }

  if (experimentSocialCuesParticipating) {
    trackingInfo.segment = {
      ...trackingInfo.segment,
      social_cues_web_cohort: experimentSocialCues ? 'variation' : 'control',
      social_cues_top_30_properties:
        toValue(getPropertiesWithSocialCues)
          ?.map((p) => p.id)
          .join('|') || '',
      social_cues_top_30_counters:
        toValue(getPropertiesWithSocialCues)
          ?.map((p) => socialCues.getSocialCuesCount(p?.whosGoing?.total))
          .join('|') || '',
    };
  }

  if (shouldFetchCityOgEvents && cityOgEventsList.length) {
    trackingInfo.segment = {
      ...trackingInfo.segment,
      occasion_genius_events: cityOgEventsList
        .map((event) => event.eventId)
        .join('|'),
      occasion_genius_event_city: cityObj?.name,
      occasion_genius_event_country: cityObj?.country,
    };
  }

  // COD-877 / COD-1067: 3PI experiment fields on Destination Landing Page Viewed.
  trackingInfo.segment = {
    ...trackingInfo.segment,
    'total_third_party_properties': cityThreePiData?.totalCount || 0,
    '3pi_cohort': experimentThreePiProperties ? 'variation' : 'control',
    '3pi_city_flag': !!cityThreePiData?.isExperimentCity,
    'static_3pi_catalog_api_flag': experimentThreePiCatalogApi ? 'variation' : 'control',
    'number_hw_results': hwResultCount,
  };

  cityPageSegmentVariables = trackingInfo.segment;

  $tracking?.trackOnPageLoad(trackingInfo.gtm, trackingInfo.segment);

  if (cityThreePiData?.hasError) {
    $tracking?.onThirdPartyError('API');
  }

  if (cityThreePiData?.properties?.length) {
    $tracking?.onThirdPartyResultsLoaded({
      ...trackingInfo.segment,
      page_number: cityAllProperties?.page || 1,
      third_party_properties_estimated: cityThreePiData?.estimatedCount || 0,
      total_third_party_properties: cityThreePiData?.totalCount || 0,
      third_party_properties_loaded: cityThreePiData.properties.length,
      third_party_page_position_start: getThreePiPositionStart(),
      ...extractThreePiPropertyData(cityThreePiData.properties),
    });
  }
});
</script>

<script>
/* eslint-disable */
import { ca } from "@/lib/channelAttribution.js";

if (import.meta.client) {
  const currentUrl = window.location.href;
  const urlObj = new URL(currentUrl);
  const entryPageNameJs = urlObj.pathname.includes("/f/") ? "FILTER" : "CITY";

  ca?.setChannelAttribution(null, entryPageNameJs);
}
/* eslint-enable */
</script>

<template>
  <ClientOnly>
    <LazyCommonHwAppDownloadBanner v-bind="getAppDownloadDetails" />
  </ClientOnly>

  <CommonHwHeader
    :free-cancellation-text="
      hasFreeCancellation
        ? $t('t_FREECANCELLATIONDIAVARIABLE', {
          DAYSINADVANCE: getFreeCancellationDay,
        })
        : ''
    "
    :headline="getMainTitle"
    :sub-label="getPropertiesCountText"
  />

  <section class="hw-wrapper headline-section">
    <WdsBreadCrumbs
      class="headline-breadcrumbs"
      :breadcrumbs="crumbs"
      :max-crumbs-shown="getCrumbsShownByViewport"
      :reversed="false"
    />
    <CommonHwLinkupsCollapsible
      v-if="
        isSmallOrMediumScreen
          && experimentSocialCuesCityBlockParticipating
          && experimentSocialCuesCityBlock
          && hasProperties
      "
      :location="cityObj"
      :city-social-cues-count="cityDisplayableSocialCues"
    />
    <div v-if="hasProperties" class="headline-map">
      <WdsPillActive
        class="headline-map-button"
        v-bind="getMapButtonParams"
        @click="handleMapButtonClick"
      />
    </div>
  </section>

  <section
    class="hw-wrapper middle-section"
    :class="{
      'with-social-cues':
        experimentSocialCuesCityBlockParticipating
        && experimentSocialCuesCityBlock,
      'with-filters': hasFilterOptions,
      'with-map': experimentCityMap && showMap,
    }"
  >
    <CommonHwLinkupsCollapsible
      v-if="
        isLargeScreen
          && experimentSocialCuesCityBlockParticipating
          && experimentSocialCuesCityBlock
          && hasProperties
      "
      class="collapsible-container"
      :location="cityObj"
      :city-social-cues-count="cityDisplayableSocialCues"
    />

    <CommonHwNoResults
      :has-results="hasProperties"
      :property-type-obj="propertyTypeObj"
      :location-name="cityObj?.name"
      :location-name-parent="cityObj?.country"
      :has-other-locations="hasOtherLocations"
    />

    <CommonHwFilterOptions
      v-if="hasFilterOptions"
      class="filters-container"
      :property-type-obj="propertyTypeObj"
      :location-info="cityObj"
      :active-filter="activeFilter"
    />
    <CommonHwPropertyCardList
      v-if="hasProperties"
      class="property-list-container"
      :property-list="cityAllProperties?.properties || []"
      :featured-properties="cityFeaturedProperties.properties"
      :total-pages="cityAllProperties?.numberOfPages"
      :current-page="cityAllProperties?.page || 1"
      :property-type-obj="propertyTypeObj"
      :location-info="cityFullLocationInfo"
      :active-filter="activeFilter"
      @sustainability-pin-click="handleSustainabilityPinClick"
      @linkups-events-click="onLinkupsPillClick"
      @whos-staying-click="handleSocialCuesClick"
      @third-party-property-click="handleThirdPartyPropertyClick"
    />
    <CommonHwSidebar
      v-if="!experimentCityMap || !showMap"
      class="sidebar-container"
      :compact-usps="!!socialHubViewModel"
      :scroll-with-page="!!socialHubViewModel"
    >
      <CityHwSocialHub
        v-if="socialHubViewModel"
        :view-model="socialHubViewModel"
        :srp-href="socialHubSrpHref"
      />
    </CommonHwSidebar>
    <Client-Only>
      <div
        v-if="experimentCityMap && !isSmallOrMediumScreen && showMap"
        class="city-map-container"
      >
        <CommonHwMap
          :property-type-obj="propertyTypeObj"
          :map-config="getMapConfigs"
          :marker-list="mapMarkers"
          :apply-clusters="false"
        />
        <div class="city-map-property-card" />
      </div>
    </Client-Only>
  </section>

  <div
    v-if="socialHubViewModel"
    class="hw-wrapper social-hub-below-list"
    :class="{ 'is-map-open': experimentCityMap && showMap }"
  >
    <CityHwSocialHub
      :view-model="socialHubViewModel"
      :srp-href="socialHubSrpHref"
    />
  </div>

  <EventsHwEventsInLocation
    v-if="cityOgEventsList.length"
    class="hw-wrapper city-section"
    :event-list="cityOgEventsList"
    :city-name="cityObj?.name"
    :location-data="cityOgEventsLocationData"
  />

  <CityHwTopPropertiesByFilter
    v-if="
      topPropertiesByCityIdPerFilters
        && Object.keys(topPropertiesByCityIdPerFilters).length
        && !activeFilter
    "
    class="hw-wrapper top-filter-section"
    filter="private-room"
    :title="
      $t('t_BESTPROPTYPEBYFILTERINCITY', {
        PROPERTYTYPE: $t(propertyTypeObj?.transcode).toLowerCase(),
        FILTERTEXT: `<span>${$t('t_TEXTFILTERPRIVATEROOMS')}</span>`,
        CITYNAME: `${cityObj?.name}`,
      })
    "
    :top-properties-by-filters="topPropertiesByCityIdPerFilters"
  />

  <CityHwTopPropertiesByFilter
    v-if="
      topPropertiesByCityIdPerFilters
        && Object.keys(topPropertiesByCityIdPerFilters).length
        && !activeFilter
    "
    class="hw-wrapper top-filter-section"
    filter="solo-travellers"
    :title="
      $t('t_BESTPROPTYPEBYFILTERINCITY', {
        PROPERTYTYPE: $t(propertyTypeObj?.transcode).toLowerCase(),
        FILTERTEXT: `<span>${$t('t_TEXTFILTERSOLO')}</span>`,
        CITYNAME: `${cityObj?.name}`,
      })
    "
    :top-properties-by-filters="topPropertiesByCityIdPerFilters"
  />
  <CityHwAboutSection
    v-if="aboutCityDescription?.processedContent && !activeFilter"
    class="hw-wrapper city-section"
    :city-name="cityObj?.name"
    :city-description="aboutCityDescription.processedContent"
    :rating-breakdown="cityObj?.ratingBreakdown"
  />

  <CityHwPopularPlaces
    v-if="hasPopularPlaces"
    class="hw-wrapper city-section"
    :property-type-obj="propertyTypeObj"
    :city-info="cityFullLocationInfo"
    :popular-places="cityPopularPlacesList"
    :title="
      $t('t_POPULARPLACESINCITY', { CITYNAME: `<span>${cityObj.name}</span>` })
    "
  />

  <section
    v-if="hasFaq || hasOtherLocations"
    class="hw-wrapper city-section faq-and-other-locations-section split-view"
  >
    <CommonHwFaq
      v-if="hasFaq && isFirstPage && !activeFilter"
      :content="getFaqContent"
    />
    <CommonHwOtherLocationsInLocation
      v-if="hasOtherLocations"
      :location-name="cityObj?.country"
      :other-locations="cityOtherLocations"
      :property-type-obj="propertyTypeObj"
    />
  </section>

  <CommonHwLanguagePicker v-show="languageStore.isLanguagePickerOpen" />

  <WdsSheet
    v-if="experimentCityMap && isSmallOrMediumScreen && showMap"
    class="city-map-sheet"
    :is-open="showMap"
    :heading="$t('t_MAP')"
    :close-button-aria-label="$t('t_A11YSHEETCLOSE')"
    @sheet-close="mapSheetIsReady = showMap = false"
    @ready="mapSheetIsReady = true"
  >
    <template #sheetBody>
      <div class="city-map-container">
        <CommonHwMap
          :property-type-obj="propertyTypeObj"
          :map-config="getMapConfigs"
          :marker-list="mapMarkers"
          :apply-clusters="false"
        />
        <div class="city-map-property-card" />
      </div>
    </template>
  </WdsSheet>
</template>

<style lang="scss" scoped>
.city-section {
  margin: wds-rem(72px) auto;

  @include desktop {
    margin: wds-rem(120px) auto;
  }
}

.social-hub-below-list {
  margin-top: $wds-spacing-l;

  @include desktop {
    &:not(.is-map-open) {
      display: none;
    }
  }
}

.headline-section {
  display: flex;
  flex-direction: column;

  .headline-map {
    background-image: url('~/public/map-horizontal.svg');
    background-repeat: no-repeat;
    background-position: top left;
    background-size: auto;
    padding: wds-rem(12px);
    display: flex;
    justify-content: center;
    border-radius: $wds-border-radius-m;
    box-shadow: inset 0 0 0 wds-rem(1px) $wds-color-ink-lighter;
  }

  @include desktop {
    flex-direction: row;
    gap: $wds-spacing-l;

    .headline-breadcrumbs {
      flex: 1;
    }

    .headline-map {
      width: wds-rem(384px);
      margin-inline: 0 !important;
    }
  }
}

.middle-section {
  position: relative;
  display: grid;
  grid-template-areas: 'property-list';
  gap: $wds-spacing-m 0;
  margin-top: $wds-spacing-m;

  &.with-filters {
    grid-template-areas:
      'page-filters'
      'property-list';
  }

  .collapsible-container {
    grid-area: page-collapsible;
  }

  .filters-container {
    grid-area: page-filters;
  }

  .property-list-container {
    grid-area: property-list;
  }

  .sidebar-container {
    display: none;
  }

  .city-map-container {
    display: none;
  }

  @include desktop {
    grid-template-areas: 'property-list side-bar';
    gap: $wds-spacing-m $wds-spacing-xxl;
    grid-template-columns: 1fr wds-rem(384px);

    &.with-social-cues {
      grid-template-areas:
        'page-collapsible side-bar'
        'property-list side-bar';
    }

    &.with-filters {
      grid-template-areas:
        'page-filters side-bar'
        'property-list side-bar';
    }

    &.with-social-cues.with-filters {
      grid-template-areas:
        'page-collapsible side-bar'
        'page-filters side-bar'
        'property-list side-bar';
    }

    &.with-map {
      grid-template-areas: 'property-list map';
      grid-template-columns: 1fr 50%;
    }

    &.with-filters.with-map {
      grid-template-areas:
        'page-filters map'
        'property-list map';
    }

    .sidebar-container {
      display: block;
      grid-area: side-bar;
      padding-top: $wds-spacing-l;
    }

    .city-map-container {
      display: block;
      grid-area: map;
      height: calc(100vh - #{wds-rem(124px)});
      top: wds-rem(104px);
      width: 100%;
      background-color: $wds-color-ink-lighter;
      position: sticky;
      border-radius: $wds-border-radius-l;
      overflow: hidden;
    }
  }
}

.faq-and-other-locations-section {
  display: grid;
  grid-auto-flow: row;
  grid-auto-columns: 1fr;
  gap: wds-rem(72px);
  align-items: flex-start;

  @include desktop {
    &.split-view {
      gap: wds-rem(40px);
      grid-auto-flow: column;
    }
  }
}

.city-map-sheet {
  :deep(sheet-body) {
    position: relative;
  }

  .city-map-container {
    display: block;
    grid-area: map;
    height: calc(100vh - #{wds-rem(124px)});
    top: wds-rem(104px);
    width: 100%;
    background-color: $wds-color-ink-lighter;
    position: sticky;
    border-radius: $wds-border-radius-l;
    overflow: hidden;
  }
}

.city-map-property-card {
  position: absolute;
  bottom: $wds-spacing-m;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  width: wds-rem(328px) !important;
}

.top-filter-section {
  margin: wds-rem(52px) auto;

  @include desktop {
    margin: wds-rem(50px) auto 0;
  }
}
</style>
